import { noticeError } from '@ratehub/base-ui';

import CARD_TYPES from '../definitions/CardTypes';
import REWARD_TYPES from '../definitions/RewardTypes';
import TRAVEL_FEATURES from '../definitions/TravelFeatures';
import DEFAULT_CARD_PREFERENCES from '../definitions/DefaultCardPreferences';
import getProductType from './getProductType';
import getMonthlySpending from './getMonthlySpending';
import getCreditScoreRange from './getCreditScoreRange';
import replaceShortcodesInRatehubReview from './replaceShortcodesInRatehubReview';
import getApprovalLikelihoodFromAPIResponse from './getApprovalLikelihoodFromAPIResponse';


/**
 * Creates a CreditCardShape from an API response and user data
 * @param {object} card - raw card data from fetch
 * @param {object} [userInfo=DEFAULT_CARD_PREFERENCES] - contextual user information, often as provided by CardFinder wizard
 * @param {boolean} [isValidApprovalLikelihood=false] - did the BE have a valid approval likelihood document?
 * @returns {(object|null)} CreditCardShape structure when successful
 */
function createCardInfoFromAPIResponse(
    card,
    userInfo = DEFAULT_CARD_PREFERENCES,
    isValidApprovalLikelihood = false
) {
    // always calculate new monthly spending; both monthly spending properties might not be present in userInfo
    const updatedSpending = getMonthlySpending(
        {
            monthlySpending: userInfo.monthlySpending ?? DEFAULT_CARD_PREFERENCES.monthlySpending,
            monthlySpendingAverage: userInfo.monthlySpendingAverage ?? DEFAULT_CARD_PREFERENCES.monthlySpendingAverage,
        },
        DEFAULT_CARD_PREFERENCES
    );

    // ensure nothing is missing by overwriting the defaults as necessary
    const userInfoWithDefaults = {
        ...DEFAULT_CARD_PREFERENCES,
        ...userInfo,
        ...updatedSpending,
    };

    return [
        createCommonProperties,
        createCashBackProperties,
    ].reduce((cardProperties, createFcn) => ({
        ...cardProperties,
        ...createFcn(card, userInfoWithDefaults, cardProperties, isValidApprovalLikelihood),
    }), {});
}


/**
 * Map back-end card properties to FE “CreditCardShape” structure, with default values for some.
 * @param {object} card - BE data for a single credit card
 * @param {object} userInfo - contextual user information, often collected by CardFinder
 * @param {any} _ignored - due for refactoring; this is unused here
 * @param {boolean} isValidApprovalLikelihood - did the BE have a valid approval likelihood document?
 * @returns {object} contains credit card properties for the given card that are common to all product types
 */
// TODO refactor
// eslint-disable-next-line max-params
function createCommonProperties(card, userInfo, _ignored, isValidApprovalLikelihood) {
    const {
        monthlySpendingAverage,
        lowInterestMonthlyBalance,
        balanceTransferAmount,
        securedMonthlyBalance,
        chosenFeatures,
    } = userInfo;

    const productType = getProductType(card.types.comparison);

    // ••• Remember to update extractRhAnalyticsFromCreditCard() when adding new properties,
    // ••• but only if the new field is relevant to analytics.

    const annualFee = card.fees.subsequent_year_annual;
    const firstYearFee = card.fees.first_year_annual;
    const purchaseInterest = card.purchase_interest?.rate ?? null;

    // within the return below, we also add subsequentYearPointsInDollars, based on a property of rewardProperties
    const rewardProperties = getRewardsProperties({
        calculatedRewards: card.calculated_rewards,
        pointSystem: card.point_system,
        monthlySpendingAverage,
    });

    const bonuses = card.calculated_rewards?.bonuses;


    return {
        id: card.ids?.primary,
        primaryId: card.ids?.primary,
        name: card.name,
        slug: card.slug, // localized by response
        bank: card.provider.ids.primary, // sometimes referred to as 'provider'; slug
        providerName: card.provider.name, // using updated terminology: provider, not bank, as historically above
        network: card.network, // a slug
        productType: productType,
        // types.display provided by mixed card endpoints and reflects the requested type in that mix
        displayType: card.types?.display ?? productType,

        rank: card.rank ?? 0,

        isMonetized: !!card.monetized,
        // sometimes sponsored is a boolean when it is used as an override (e.g. featured cards)
        isSponsored: typeof(card.sponsored) === 'boolean'
            ? card.sponsored
            : !!card.sponsored?.table,

        cardImageURL: card.urls?.image,
        detailsURL: card.urls?.details,
        applyURL: card.urls?.apply ?? '',
        applyRedirectURL: card.urls?.apply_redirect ?? '',

        // undefined or null each map to null (will show N/A in footer); 0% is now allowed
        purchaseInterest,
        purchaseInterestMax: card.purchase_interest?.rate_max ?? null,
        cashAdvanceInterest: card.cash_advance_interest?.rate ?? null,
        cashAdvanceInterestMax: card.cash_advance_interest?.rate_max ?? null,
        balanceTransferInterest: card.balance_transfer_interest?.rate ?? null,
        balanceTransferInterestMax: card.balance_transfer_interest?.rate_max ?? null,

        ...getApprovalLikelihoodForCreditCard(
            isValidApprovalLikelihood,
            card.approval_likelihood_raw,
            getCardTypesFromAPI(card),
        ),

        ...getOffer(card.gift_cards),
        hasReferralOffer: !!card.referral_offer,
        ...((!!card.referral_offer && typeof card.referral_value === 'number') && {
            referralOfferValue: card.referral_value,
        }),

        earningsDescription: Array.isArray(card.descriptions?.rewards)
            ? card.descriptions.rewards.map(rewardsDesc => {
                return {
                    rate: rewardsDesc.rate,
                    types: rewardsDesc.categories,
                    description: rewardsDesc.copy,
                };
            })
            : [],

        ...(card?.eligibility && (card?.eligibility?.deposit >= 0)
            && { minimumDeposit: parseFloat(card.eligibility.deposit) }), // secured cards

        annualFee: annualFee,
        firstYearFee: firstYearFee,
        isFirstYearFeeWaived: !!(annualFee && !firstYearFee), // Note: will be false if first and subsequent are both 0

        bestUsedFor: card.descriptions?.best_used_for?.trim(),
        description: card.descriptions?.badge?.trim(),
        welcomeBonusDescription: card.descriptions?.badge_bonus?.trim(),
        anniversaryBonusDescription: card.descriptions?.badge_anniversary_bonus?.trim(),

        anniversaryBonus: {
            points: bonuses?.anniversary.points,
            value: bonuses?.anniversary.value,
            maxPoints: bonuses?.max_anniversary?.points
                ?? bonuses?.anniversary.points,
            maxValue: bonuses?.max_anniversary?.value
                ?? bonuses?.anniversary.value,
        },

        pointSystem: {
            dollarValue: card.point_system?.dollar_values,
            name: card.point_system?.name,
            id: card.point_system?.ids.primary,
        },

        cardTypes: getCardTypesFromAPI(card),

        rewardTypes: getRewardTypesFromAPI(card),
        userCategory: card.user_category,

        ...rewardProperties,
        subsequentYearPointsInDollars:
            Math.floor(rewardProperties.totalSubsequentYearPoints * rewardProperties.averagePointValue),

        ...getSignupBonusProperties(bonuses),

        // if credit_score is missing, we'll set the minimumCreditScore to null, not undefined;
        minimumCreditScore: getCreditScoreRange(card.eligibility.credit_score.public)?.min ?? null,
        minimumIncome: card.eligibility.income.minimum.public || 0,

        bonusDescriptionMarkup: card.descriptions.bonus,
        ...getTravelProperties({
            pointRedemption: card.point_redemption,
            features: card.features,
            chosenFeatures,
        }),

        ...getFeatureDescriptions({ apiFeaturesList: card.features_table }),

        ...getLowInterestProperties({
            purchaseInterest,
            lowInterestMonthlyBalance,
        }),

        ...getBalanceTransferProperties({
            balanceTransferFee: card.fees?.balance_transfer,
            balanceTransferAmount,
            balanceTransferInterestPromotional: card.balance_transfer_interest.promotional,
        }),

        ...getSecuredProperties({
            purchaseInterest,
            securedMonthlyBalance,
        }),

        prohibitedProvinces: card.prohibited_provinces ?? [],

        isHidden: card.hidden,
        bulletPointSummary: Array.isArray(card.descriptions.summary)
            ? card.descriptions.summary
            : [],

        minimumHouseholdIncome: card.eligibility.income.household || 0,

        // API returns out of 5; we need out of 100.
        reviewRating: (card.user_reviews.average_rating || 0) * 20,
        reviewCount: card.user_reviews.review_count,
        reviewURL: card.urls.details + '?view=reviews',
        ratehubReview: createRatehubReviewFromAPIResponse(card),
        reviews: card.user_reviews.reviews.map(review => {
            return {
                name: review.name,
                rating: review.rating,
                comments: review.comments,
                date: new Date(review.date),
            };
        }),

        // TODO remove style removal hack once classic pages are deprecated, and if the style attribute is absent in the CMS
        // TODO conditionally retrieve this only as required (as of now, that means only from a card details page)
        legalMarkup: card.descriptions.legal?.trim().replace(/\sstyle=(['"])[^\\1]*\1/ig, '') || undefined,
    };
}


const BALANCE_TRANSFER_FEE_PERCENTAGE_DEFAULT = 3;

const API_TO_CARD_TYPE = {
    'rewards': CARD_TYPES.REWARDS,
    'low-interest': CARD_TYPES.LOW_INTEREST,
    'balance-transfer': CARD_TYPES.BALANCE_TRANSFER,
    'secured': CARD_TYPES.SECURED,
};

const API_TO_REWARD_TYPES = {
    'travel': REWARD_TYPES.TRAVEL,
    'cash-back': REWARD_TYPES.CASH_BACK,
    'store-credit': REWARD_TYPES.STORE_CREDIT,
};

const API_FEATURE_MAP = {
    'baggage-delay':        TRAVEL_FEATURES.BAGGAGE_DELAY,
    'baggage-loss':         TRAVEL_FEATURES.BAGGAGE_LOSS,
    'car-rental':           TRAVEL_FEATURES.CAR_RENTAL,
    'extended-warranty':    TRAVEL_FEATURES.EXTENDED_WARRANTY,
    'flight-delay':         TRAVEL_FEATURES.FLIGHT_DELAY,
    'lounge-access':        TRAVEL_FEATURES.LOUNGE_ACCESS,
    'mobile-insurance':     TRAVEL_FEATURES.MOBILE_INSURANCE,
    'no-forex':             TRAVEL_FEATURES.NO_FOREX,
    'price-protection':     TRAVEL_FEATURES.PRICE_PROTECTION,
    'purchase-assurance':   TRAVEL_FEATURES.PURCHASE_ASSURANCE,
    'travel-accident':      TRAVEL_FEATURES.TRAVEL_ACCIDENT,
    'travel-emergency':     TRAVEL_FEATURES.TRAVEL_EMERGENCY,
    'trip-cancellation':    TRAVEL_FEATURES.TRIP_CANCELLATION,
    'trip-interruption':    TRAVEL_FEATURES.TRIP_INTERRUPTION,
    'hotel-motel-burglary': TRAVEL_FEATURES.HOTEL_MOTEL_BURGLARY,
};


/**
 * Derives approval-likelihood values for a credit card from a raw percentage score.
 * - If not verified, both approvalLikelihood and approvalLikelihoodScore are undefined.
 * - If verified, the raw score is clamped to 0..100 and then mapped to a user-facing bucket
 *   via getApprovalLikelihoodFromAPIResponse(). If a numeric score is not provided, the
 *   approvalLikelihood and the score will be undefined.
 * @param {boolean} isVerified - Has the rawLikelihood value been verified from a valid approval likelihood document?
 * @param {number|undefined} rawApprovalLikelihood - Raw approval chance as a percentage (0..100).
 * @param {string[]} cardTypes - List of product type slugs for the card.
 * @returns {{approvalLikelihood: (string|undefined), approvalLikelihoodScore: (number|undefined)}}
 *   approvalLikelihood: one of ['unknown', ..., 'prequalified'] or undefined
 *   approvalLikelihoodScore: numeric score when verified; undefined otherwise
 */
function getApprovalLikelihoodForCreditCard(isVerified, rawApprovalLikelihood, cardTypes) {
    if (!isVerified) {
        return {
            approvalLikelihood: undefined,
            approvalLikelihoodScore: undefined,
        };
    }

    // raw approval likelihood should not exceed 100, but we have seen mistakes where it has
    const rawLikelihood = typeof rawApprovalLikelihood === 'number'
        ? Math.min(rawApprovalLikelihood, 100)
        : undefined;

    return {
        approvalLikelihood: getApprovalLikelihoodFromAPIResponse(
            true,
            rawLikelihood,
            cardTypes
        ),
        approvalLikelihoodScore: rawLikelihood,
    };
}


/**
 * Map back-end card properties for Travel cards to FE properties.
 * @param {object} arg - object argument
 * @param {object} arg.pointRedemption -
 * @param {string[]} [arg.features=[]] - api strings for each card feature
 * @param {string[]} arg.chosenFeatures - user’s wizard feature preferences
 * @returns {object} - contains travel-related properties
 */
function getTravelProperties({ pointRedemption, features = [], chosenFeatures }) {
    const cardFeatures = features.map(feature => API_FEATURE_MAP[feature]);

    return {
        redemptionFlexibility: {
            level: pointRedemption?.flexibility_level,
            description: pointRedemption?.copy.travel || '',
        },

        // features is left as an array of slugs: the intersection of card features and user’s selection
        // but if chosenFeatures is nullish, we show all card features with no filtering
        features: chosenFeatures
            ? chosenFeatures.filter(apiSlug => cardFeatures.includes(apiSlug)) || []
            : cardFeatures,
    };
}

/**
 * @typedef {object} apiCardFeature
 * @property {boolean} active - is this feature on the card?
 * @property {string} description - feature description; empty string allowed
 */
/**
 * @typedef {object} feCardFeature
 * @property {string} featureAPIKey - featureAPIKey
 * @property {string} description - feature description; empty string allowed
 */
/**
 * Based on the incoming apiCardFeature object, this returns an array of objects where each object has:
 * • an API key which will be used to search within TRAVEL_FEATURES for the value used within TravelFeatureOptions
 * • a description for the feature (could be empty)
 * @param {Record<string, apiCardFeature>} apiFeaturesList - object of features, each with a description and active
 * @returns {{featureDescriptions: {feCardFeature}[]}} -
 *  an object with a property called featureDescriptions which will be an array of objects or an empty array
 */
function getFeatureDescriptions({ apiFeaturesList }) {
    if (!apiFeaturesList) {
        return {
            featureDescriptions: [],
        };
    }

    const featureDescriptions = [];
    Object.entries(apiFeaturesList).forEach(([ key, feature ]) => {
        if (feature.active) {
            featureDescriptions.push({
                featureAPIKey: key.toUpperCase(),
                description: feature.description || '',     // description may be empty; implies 'included'
            });
        }
    });

    return {
        featureDescriptions,
    };
}


function getCardTypesFromAPI(card) {
    const cardTypes = (card.types?.primary || []).map(type => API_TO_CARD_TYPE[type]);

    if (cardTypes.some(cardType => !cardType)) {
        const cardId = card.ids?.primary;

        noticeError(new RangeError(
            `[${cardId} • ${card.name}]\n\t• Unrecognized card type in [${card.types?.primary}]`,
        ), {
            cardId: cardId,
            cardTypes: card.types?.primary,
            targetKeys: Object.keys(API_TO_CARD_TYPE),
        });
    }

    return cardTypes.filter(cardType => cardType);
}

function getRewardTypesFromAPI(card) {
    const rewardTypes = (card.types?.rewards || []).map(type => API_TO_REWARD_TYPES[type]);

    if (rewardTypes.some(rewardType => !rewardType)) {
        const cardId = card.ids?.primary;

        noticeError(new RangeError(
            `[${cardId} • ${card.name}]\n\t• Unrecognized reward type in [${card.types?.rewards}]`,
        ), {
            cardId: cardId,
            rewardTypes: card.types?.rewards,
            targetKeys: Object.keys(API_TO_REWARD_TYPES),
        });
    }

    return rewardTypes.filter(rewardType => rewardType);
}


/**
 * Get FE giftCardOffer structure from the API response.
 * The BE responds with an array, since in early days there could be multiple gift offers, but that is not our current practice.
 * The description and dollar value seen in the FE consider only the first offer found.
 * Originally, this always represented a gift card (hence the name gift*Card*Offer), but it may be other offer types.
 * @param {object[]} gift_cards - gift card array from API response
 * @returns {{giftCardOffer: {GiftOfferShape}}|undefined} - gift offer if present
 */
function getOffer(gift_cards) {
    // BE still replies with an array, but we only use the first item
    const firstCard = gift_cards?.[0];

    return firstCard?.description && firstCard?.amount && (firstCard?.name || firstCard?.offer_type)
        ? {
            giftCardOffer: {
                promoDetailsHTML: firstCard.description,
                maxDollarValue: firstCard.amount,

                offerType: firstCard.offer_type,

                ...firstCard?.expiry_date && { expiryDate: new Date(firstCard.expiry_date) },
            },
        }
        : undefined;
}


/**
 * Map back-end card properties for Rewards cards to FE properties.
 * @param {object} arg - object argument
 * @param {object} arg.calculatedRewards - back-end data for a single credit card
 * @param {object} arg.pointSystem - back-end data for a card’s point system
 * @param {number} arg.monthlySpendingAverage - the member’s monthly spending average
 * @returns {object} - contains rewards-related properties
 */
function getRewardsProperties({ calculatedRewards, pointSystem, monthlySpendingAverage }) {
    return calculatedRewards === undefined
        ? {
            netFirstYearValue: null,
            netSubsequentYearValue: null,
            totalSubsequentYearPoints: null,

            welcomeSignUp: null,
            welcomeSpending: null,

            firstYearPointsInDollars: null,
            firstYearRewardPoints: null,

            averagePointValue: null,
            monthlySpendingAverage,
        }
        : {
            netFirstYearValue: calculatedRewards.first_year.net_value,
            netSubsequentYearValue: calculatedRewards.subsequent_years.net_value,

            totalSubsequentYearPoints: Math.floor(
                calculatedRewards.subsequent_years.tiers.reduce(
                    (sum, { points }) => sum + points,
                    0,      // start at 0; do not roll anniversary points in here
                ),
            ),

            welcomeSignUp: {
                points: calculatedRewards.bonuses?.welcome_signup.points,
                value: Math.floor(calculatedRewards.bonuses?.welcome_signup.value),
            },

            welcomeSpending: {
                points: calculatedRewards.bonuses?.welcome_spending.points,
                value: Math.floor(calculatedRewards.bonuses?.welcome_spending.value),
            },

            firstYearPointsInDollars: Math.floor(calculatedRewards.first_year.tiers
                .reduce((sum, { value }) => sum + value, 0)),

            firstYearRewardPoints: Math.floor(calculatedRewards.first_year.tiers
                .reduce((sum, { points }) => sum + points, 0)),

            // currently only used by card details
            averagePointValue: Math.max(...Object.values(pointSystem.dollar_values)),

            monthlySpendingAverage,
        };
}

/**
 * Sums Welcome signup and Welcome spending bonuses for each of points and dollars.
 * @param {Record<string, object>} bonuses - backend-calculated reward bonuses for different categories
 * @returns {{signUpBonusPoints: number, signUpBonusDollars: number,
 *          maxSignUpBonusPoints: number, maxSignUpBonusDollars: number,
 *          welcomeBonus: object} | undefined} - summary of sign up bonuses
 */
function getSignupBonusProperties(bonuses) {
    if (!bonuses) {
        return undefined;
    }

    const welcomeSignupPoints = bonuses.welcome_signup?.points || 0;
    const welcomeSignupValue = bonuses.welcome_signup?.value || 0;

    const welcomeSpendingPoints = bonuses.welcome_spending?.points || 0;
    const welcomeSpendingValue = bonuses.welcome_spending?.value || 0;

    const maxWelcomeSignupPoints = bonuses.max_welcome_signup?.points || welcomeSignupPoints;
    const maxWelcomeSignupValue = bonuses.max_welcome_signup?.value || welcomeSignupValue;

    const maxWelcomeSpendingPoints = bonuses.max_welcome_spending?.points || welcomeSpendingPoints;
    const maxWelcomeSpendingValue = bonuses.max_welcome_spending?.value || welcomeSpendingValue;

    const welcomeBonus = bonuses.details;

    const signUpBonusPoints = welcomeSignupPoints + welcomeSpendingPoints;
    const signUpBonusDollars = Math.floor(welcomeSignupValue + welcomeSpendingValue);
    const maxSignUpBonusPoints = maxWelcomeSignupPoints + maxWelcomeSpendingPoints;
    const maxSignUpBonusDollars = Math.floor(maxWelcomeSignupValue + maxWelcomeSpendingValue);

    return {
        signUpBonusPoints,
        signUpBonusDollars,
        maxSignUpBonusPoints,
        maxSignUpBonusDollars,
        welcomeBonus,
    };
}


function getLowInterestProperties({ purchaseInterest, lowInterestMonthlyBalance }) {
    if (purchaseInterest !== null && purchaseInterest >= 0 && lowInterestMonthlyBalance >= 0) {
        const lowInterestAnnualInterestFee = lowInterestMonthlyBalance * purchaseInterest / 100;

        return {
            lowInterestAnnualInterestFee: lowInterestAnnualInterestFee,
            lowInterestMonthlyInterestFee: lowInterestAnnualInterestFee / 12,

            lowInterestMonthlyBalance,
        };
    } else {
        return {};
    }
}


function getBalanceTransferProperties({
    /**
     * map back-end card properties for Balance Transfer cards to “cardInfo” property names, with default values for some
     * @param {object} arg - object argument
     * @param {number} arg.balanceTransferFee - fee for balance transfers
     * @param {number} arg.balanceTransferAmount - how much the user wants to transfer to a new card
     * @param {number} arg.balanceTransferInterestPromotional - balance transfer promotional information
     * @param {number} balanceTransferInterestPromotional.term - months of offer
     * @param {number} balanceTransferInterestPromotional.rate - special rate during offer
     * @returns {object} - contains balance transfer-related properties
     */
    balanceTransferFee,
    balanceTransferAmount,
    balanceTransferInterestPromotional,
}) {
    // balanceTransferPromoMonths used below to conditionally spread additional dependent props
    const balanceTransferPromoMonths = balanceTransferInterestPromotional.term;
    const balanceTransferPromoInterest = balanceTransferPromoMonths
        ? balanceTransferInterestPromotional.rate || 0
        : undefined;

    let balanceTransferFeeRate,
        balanceTransferPromoMonthlyInterestFee;

    if (
        balanceTransferAmount >= 0
        && balanceTransferPromoInterest >= 0
        && balanceTransferPromoMonths > 0
    ) {
        // balanceTransferFeeRate used below to conditionally spread additional dependent props
        balanceTransferFeeRate = balanceTransferFee ?? BALANCE_TRANSFER_FEE_PERCENTAGE_DEFAULT;
        balanceTransferPromoMonthlyInterestFee = balanceTransferAmount * (balanceTransferPromoInterest / 100 / 12);
    }

    return {
        ...(typeof balanceTransferPromoMonths !== 'undefined' && {
            balanceTransferPromoInterest,
            balanceTransferPromoMonths,
        }),

        ...(typeof balanceTransferFeeRate !== 'undefined' && {
            balanceTransferAmount,
            balanceTransferFeeRate,
            balanceTransferFee: balanceTransferAmount * balanceTransferFeeRate / 100,
            balanceTransferMonthlyPayment:
                balanceTransferAmount / balanceTransferPromoMonths + balanceTransferPromoMonthlyInterestFee,
        }),
    };
}


function getSecuredProperties({ purchaseInterest, securedMonthlyBalance }) {
    return {
        ...(purchaseInterest >= 0 && securedMonthlyBalance >= 0
            && { securedMonthlyInterestFee: securedMonthlyBalance * purchaseInterest / 100 / 12 }),

        ...(securedMonthlyBalance >= 0
            && { securedMonthlyBalance: parseFloat(securedMonthlyBalance) }),
    };
}


/**
 * Re-shape a review into the format our component expects.
 * @param {object} card - API response for a given credit card
 * @returns {object|null} - Ratehub review
 */
function createRatehubReviewFromAPIResponse(card) {
    const reviewInfo = card.ratehub_review;
    if (!reviewInfo) {
        return null;
    }

    // Reshape it from the API into the way our Component wants it.
    return {
        content: replaceShortcodesInRatehubReview(card),
        rating: reviewInfo.rating,
        reviewAuthor: {
            name: reviewInfo.author?.name,
            jobTitle: reviewInfo.author?.job_title,
            profileURL: reviewInfo.author?.profile_url ,
            avatarImage: reviewInfo.author?.user?.user_avatar,    // currently useless?
            profileImage: reviewInfo.author?.image_url,
        },
        publishedDate: reviewInfo.dates?.published,
        modifiedDate: reviewInfo.dates?.modified,
    };
}


function createCashBackProperties({ point_redemption }, _ignored, { welcomeBonus }) {
    return point_redemption?.copy.cash_back
        ? {
            howToRedeemDescription: point_redemption.copy.cash_back,

            ...(point_redemption.flexibility_level && {
                redemptionFlexibility: {
                    level: point_redemption.flexibility_level,
                    description: point_redemption.copy.cash_back,
                },
            }),

            // expected sub-objects: welcome_signup and welcome_spending, each with an optional
            // property 'cash-back-rewards'
            cashBackWelcomeBonus: Object.values(welcomeBonus || {})
                .reduce((sum, entry) => sum + (entry['cash-back-rewards'] || 0), 0),
        }
        : {};
}


export default createCardInfoFromAPIResponse;
